// /tables — Table Management (PAGES.md §3.26)
//
// CRUD management for dining tables. Create, edit, delete tables.
// Assign capacity, section/floor, and shape.
// RESTAURANT vertical only — MANAGER+ (level 30).

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Badge,
  DataTable,
  Modal,
  Input,
  Select,
  Toggle,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  ConfirmDialog,
  Tooltip,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { tableService } from '@/services/table.service';
import type { DiningTable, CreateTablePayload, UpdateTablePayload, TableShape, TableStats } from '@/services/table.service';
import { useAuthStore } from '@/stores/use-auth-store';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { resolveTableLocationId } from './resolve-table-location';
import { buildUpdateTablePayload } from './update-table-payload';
import { deriveTableKpis } from './table-stats';
import { GLASS, cn, toneClasses } from '@/constants/design-tokens';
import { ApiError } from '@/lib/api-client';

// ─── Icons ──────────────────────────────────────────────────────────────────

function LayoutIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h18v18H3V3zm0 6h18M9 9v12" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function PencilIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125" />
    </svg>
  );
}

function TrashIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}

function FloorPlanIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
    </svg>
  );
}

// ─── Constants ──────────────────────────────────────────────────────────────

const ROLE_LEVELS: Record<string, number> = {
  SUPER_ADMIN: 100, ADMIN: 80, MODERATOR: 60, OWNER: 50, MANAGER: 30, CAPTAIN: 7, CASHIER: 5,
};

const SHAPE_OPTIONS = [
  { value: 'rectangle', label: 'Rectangle' },
  { value: 'circle', label: 'Round' },
  { value: 'square', label: 'Square' },
];

const SHAPE_BADGE: Record<string, { label: string; variant: 'active' | 'info' | 'pending' }> = {
  rectangle: { label: 'Rectangle', variant: 'info' },
  circle: { label: 'Round', variant: 'pending' },
  square: { label: 'Square', variant: 'active' },
};

const EMPTY_FORM: FormState = {
  table_number: '',
  seating_capacity: 2,
  floor: 'Ground',
  section: '',
  shape: 'rectangle',
  is_active: true,
};

// ─── Types ──────────────────────────────────────────────────────────────────

interface FormState {
  table_number: string;
  seating_capacity: number;
  floor: string;
  section: string;
  shape: TableShape;
  is_active: boolean;
}

interface FormErrors {
  table_number?: string;
  seating_capacity?: string;
  floor?: string;
}

// ─── KPI Card ───────────────────────────────────────────────────────────────

function KpiMini({ label, value, tone }: { label: string; value: string | number; tone: 'brand' | 'success' | 'violet' | 'teal' }) {
  const tc = toneClasses(tone);
  return (
    <div className={cn(GLASS, 'relative overflow-hidden p-4')}>
      <div className={`absolute inset-0 bg-gradient-to-br ${tc.accent} pointer-events-none`} />
      <p className="relative text-xs font-medium text-neutral-500 dark:text-neutral-400">{label}</p>
      <p className="relative text-xl font-bold text-neutral-900 dark:text-neutral-100 tabular-nums font-mono mt-1">{value}</p>
    </div>
  );
}

// ─── Validation ─────────────────────────────────────────────────────────────

function validateForm(form: FormState): FormErrors {
  const errors: FormErrors = {};
  if (!form.table_number.trim()) errors.table_number = 'Table number is required';
  else if (form.table_number.length > 20) errors.table_number = 'Max 20 characters';
  if (form.seating_capacity < 1) errors.seating_capacity = 'Minimum 1 seat';
  else if (form.seating_capacity > 50) errors.seating_capacity = 'Maximum 50 seats';
  if (form.floor.length > 50) errors.floor = 'Max 50 characters';
  return errors;
}

// ─── Component ──────────────────────────────────────────────────────────────

function TableManagementContent() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const role = useAuthStore((s) => s.profile?.role ?? '');
  const roleLevel = ROLE_LEVELS[role] ?? 0;
  const canDelete = roleLevel >= ROLE_LEVELS.OWNER;
  const storeLocationId = useDashboardStore((s) => s.selectedLocationId);
  const profileLocations = useAuthStore((s) => s.profile?.locations);
  // The dashboard location filter defaults to "All locations" (undefined).
  // A dining table must belong to a concrete location, so fall back to the
  // user's first active location instead of sending an empty location_id,
  // which the API rejects (GitHub issue #71).
  const selectedLocationId = resolveTableLocationId(storeLocationId, profileLocations);

  // ── State ───────────────────────────────────────────────────────────────
  const [tables, setTables] = useState<DiningTable[]>([]);
  const [stats, setStats] = useState<TableStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;

  const [modalOpen, setModalOpen] = useState(false);
  const [editingTable, setEditingTable] = useState<DiningTable | null>(null);
  const [form, setForm] = useState<FormState>({ ...EMPTY_FORM });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isSaving, setIsSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<DiningTable | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // ── Fetch ───────────────────────────────────────────────────────────────
  const fetchTables = useCallback(async () => {
    setIsLoading(true);
    try {
      const [result, statsResult] = await Promise.all([
        tableService.list({
          location_id: selectedLocationId,
          page: currentPage,
          limit: pageSize,
        }),
        tableService.getStats({ location_id: selectedLocationId }),
      ]);
      setTables(result.data);
      setTotalCount(result.meta?.total ?? result.data.length);
      setStats(statsResult);
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to load tables.', variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [selectedLocationId, currentPage, toast]);

  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  // ── KPI ─────────────────────────────────────────────────────────────────
  // Derived from the location-wide `/dining-tables/stats` aggregate (not the
  // current page slice) so the cards reflect every table and stay constant as
  // you paginate (GitHub issue #278).
  const kpis = deriveTableKpis(stats);

  // ── Modal ───────────────────────────────────────────────────────────────
  const openCreateModal = useCallback(() => {
    setEditingTable(null);
    setForm({ ...EMPTY_FORM });
    setFormErrors({});
    setModalOpen(true);
  }, []);

  // ── Keyboard shortcut: n → Add table ────────────────────────────────────
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      const tag = target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) return;
      if (e.key === 'n' && !modalOpen && !deleteTarget) {
        e.preventDefault();
        openCreateModal();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modalOpen, deleteTarget, openCreateModal]);

  const openEditModal = useCallback((table: DiningTable) => {
    setEditingTable(table);
    setForm({
      table_number: table.table_number,
      seating_capacity: table.seating_capacity ?? 2,
      floor: table.floor ?? 'Ground',
      section: table.section ?? '',
      shape: (table.shape?.toLowerCase() as TableShape) ?? 'rectangle',
      is_active: table.is_active,
    });
    setFormErrors({});
    setModalOpen(true);
  }, []);

  const handleFormChange = useCallback((field: keyof FormState, value: unknown) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  }, []);

  const handleSave = useCallback(async () => {
    const errors = validateForm(form);
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }
    setIsSaving(true);
    try {
      if (editingTable) {
        const payload: UpdateTablePayload = buildUpdateTablePayload({
          tableNumber: form.table_number,
          seatingCapacity: form.seating_capacity,
          floor: form.floor,
          section: form.section,
          shape: form.shape,
          isActive: form.is_active,
        });
        await tableService.update(editingTable.id, payload);
        toast({ title: `Table ${form.table_number} saved`, variant: 'success' });
      } else {
        if (!selectedLocationId) {
          toast({
            title: 'Select a location before adding a table.',
            variant: 'error',
          });
          return;
        }
        const payload: CreateTablePayload = {
          table_number: form.table_number,
          seating_capacity: form.seating_capacity,
          floor: form.floor,
          shape: form.shape,
          location_id: selectedLocationId,
        };
        await tableService.create(payload);
        toast({ title: `Table ${form.table_number} created`, variant: 'success' });
      }
      setModalOpen(false);
      await fetchTables();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.statusCode === 409) {
          setFormErrors({ table_number: 'Table number already exists' });
        } else {
          toast({ title: err.message, variant: 'error' });
        }
      } else {
        toast({ title: 'Failed to save table', variant: 'error' });
      }
    } finally {
      setIsSaving(false);
    }
  }, [form, editingTable, selectedLocationId, fetchTables, toast]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await tableService.delete(deleteTarget.id);
      toast({ title: `Table ${deleteTarget.table_number} deleted`, variant: 'success' });
      setDeleteTarget(null);
      await fetchTables();
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to delete table', variant: 'error' });
    } finally {
      setIsDeleting(false);
    }
  }, [deleteTarget, fetchTables, toast]);

  // ── Columns ─────────────────────────────────────────────────────────────
  const columns = useMemo<ColumnDef<DiningTable>[]>(() => [
    {
      key: 'table_number',
      header: 'Table #',
      render: (_val: unknown, row: DiningTable) => (
        <span className="font-bold text-neutral-900 dark:text-neutral-100">{row.table_number}</span>
      ),
    },
    {
      key: 'seating_capacity',
      header: 'Capacity',
      numeric: true,
      render: (_val: unknown, row: DiningTable) => (
        <span className="font-mono tabular-nums text-neutral-700 dark:text-neutral-300">
          {row.seating_capacity ?? '—'} <span className="text-neutral-400 text-xs">seats</span>
        </span>
      ),
    },
    {
      key: 'floor',
      header: 'Section / Floor',
      render: (_val: unknown, row: DiningTable) => (
        <span className="text-neutral-600 dark:text-neutral-400">
          {row.section ?? row.floor ?? '—'}
        </span>
      ),
    },
    {
      key: 'shape',
      header: 'Shape',
      render: (_val: unknown, row: DiningTable) => {
        const shapeKey = (row.shape?.toLowerCase() ?? 'rectangle') as string;
        const badge = SHAPE_BADGE[shapeKey] ?? SHAPE_BADGE.rectangle;
        return <Badge variant={badge.variant} size="sm">{badge.label}</Badge>;
      },
    },
    {
      key: 'is_active',
      header: 'Status',
      render: (_val: unknown, row: DiningTable) => (
        <Badge variant={row.is_active ? 'active' : 'cancelled'} size="sm">
          {row.is_active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (_val: unknown, row: DiningTable) => (
        <div className="flex items-center gap-1 justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openEditModal(row)}
            aria-label={`Edit table ${row.table_number}`}
          >
            <PencilIcon />
          </Button>
          {canDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDeleteTarget(row)}
              className="text-danger-600 hover:text-danger-700 hover:bg-danger-50 dark:hover:bg-danger-900/20"
              aria-label={`Delete table ${row.table_number}`}
            >
              <TrashIcon />
            </Button>
          )}
        </div>
      ),
    },
  ], [canDelete, openEditModal]);

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <PageContainer
      title="Table Management"
      subtitle={`${tables.length} table${tables.length !== 1 ? 's' : ''}`}
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Tables' },
      ]}
      actions={
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate('/floor-plan')}
          >
            <FloorPlanIcon />
            <span className="ml-1.5">Edit Floor Plan</span>
          </Button>
          <Tooltip content="Press N to add (keyboard shortcut)">
            <Button
              variant="primary"
              size="sm"
              onClick={openCreateModal}
            >
              <PlusIcon />
              <span className="ml-1.5">Add Table</span>
            </Button>
          </Tooltip>
        </div>
      }
    >
      <div className="animate-fade-in">
        {/* ═══ KPI Row ═══ */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KpiMini label="Total Tables" value={kpis.totalTables} tone="violet" />
          <KpiMini label="Total Capacity" value={`${kpis.totalCapacity} seats`} tone="brand" />
          <KpiMini label="Active Tables" value={kpis.activeTables} tone="success" />
          <KpiMini label="Sections" value={kpis.sections} tone="teal" />
        </div>

        {/* ═══ Data Table ═══ */}
        <div className={cn(GLASS, 'overflow-hidden')}>
          {isLoading ? (
            <div className="p-6 space-y-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex gap-4">
                  <Skeleton variant="text" width="15%" height={18} />
                  <Skeleton variant="text" width="10%" height={18} />
                  <Skeleton variant="text" width="15%" height={18} />
                  <Skeleton variant="rect" width={80} height={24} />
                  <Skeleton variant="rect" width={60} height={24} />
                </div>
              ))}
            </div>
          ) : tables.length === 0 ? (
            <div className="p-12">
              <EmptyState
                variant="inline"
                icon={<LayoutIcon className="h-14 w-14" />}
                title="No tables configured"
                description="Add your first table to start managing your restaurant floor"
                action={{
                  label: 'Add Table',
                  onClick: openCreateModal,
                }}
              />
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={tables}
              totalCount={totalCount}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      </div>

      {/* ═══ Add/Edit Modal ═══ */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingTable ? `Edit Table ${editingTable.table_number}` : 'Add Table'}
        footer={
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setModalOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} loading={isSaving}>
              {editingTable ? 'Save Changes' : 'Create Table'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {/* Table Number */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1">
              Table Number <span className="text-danger-500">*</span>
            </label>
            <Input
              value={form.table_number}
              onChange={(val) => handleFormChange('table_number', val)}
              placeholder="e.g. T1, A5, Booth 3"
              error={formErrors.table_number}
              aria-label="Table number"
            />
          </div>

          {/* Seating Capacity */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1">
              Seating Capacity <span className="text-danger-500">*</span>
            </label>
            <Input
              type="number"
              value={String(form.seating_capacity)}
              onChange={(val) => handleFormChange('seating_capacity', parseInt(val, 10) || 0)}
              min={1}
              max={50}
              error={formErrors.seating_capacity}
              aria-label="Seating capacity"
            />
          </div>

          {/* Floor */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1">
              Floor
            </label>
            <Input
              value={form.floor}
              onChange={(val) => handleFormChange('floor', val)}
              placeholder="e.g. Ground, First Floor, Rooftop"
              error={formErrors.floor}
              aria-label="Floor"
            />
          </div>

          {/* Section */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1">
              Section
            </label>
            <Input
              value={form.section}
              onChange={(val) => handleFormChange('section', val)}
              placeholder="e.g. Outdoor, VIP, Garden, Bar"
              aria-label="Section"
            />
          </div>

          {/* Shape */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1">
              Table Shape
            </label>
            <Select
              options={SHAPE_OPTIONS}
              value={form.shape}
              onChange={(val) => handleFormChange('shape', typeof val === 'string' ? val : val[0])}
              aria-label="Table shape"
            />
          </div>

          {/* Active Toggle */}
          <div className="flex items-center justify-between pt-2">
            <label className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
              Active
            </label>
            <Toggle
              checked={form.is_active}
              onChange={(checked) => handleFormChange('is_active', checked)}
              aria-label="Active status"
            />
          </div>
        </div>
      </Modal>

      {/* ═══ Delete Confirmation ═══ */}
      <ConfirmDialog
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={`Delete Table ${deleteTarget?.table_number ?? ''}?`}
        description="This action cannot be undone. The table will be permanently removed."
        confirmLabel="Delete"
        variant="danger"
        loading={isDeleting}
      />
    </PageContainer>
  );
}

// ─── Export with ErrorBoundary ───────────────────────────────────────────────

export function TableManagementPage() {
  return (
    <ErrorBoundary>
      <TableManagementContent />
    </ErrorBoundary>
  );
}
